import {useGetRecentGames} from "@hooks/gamesList/useGetRecentGames";
import {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {Carousel, CarouselGameCard, HeadingCarousel, normalizeCarouselGame} from "@/components";
import useAddFavoriteGame from "@/hooks/games/useAddFavoriteGame.hook";
import useDeleteFavoriteGame from "@/hooks/games/useDeleteFavoriteGame.hook";
import {useAuthStore} from "@/store/useAuth.store";
import {useGameMetadataStore} from "@/store/useGameMetadataStore";
import {useMiniGames} from "@/store/useMiniGames";
import styles from "./recently-played-games.styles.module.scss";

export const RecentlyPlayedGames = () => {
  const {t} = useTranslation();
  const {recentGames, isPending} = useGetRecentGames();
  const [carouselApi, setCarouselApi] = useState(null);
  const navigate = useNavigate();
  const authStatus = useAuthStore(state => state.authStatus);
  const setFavoriteGames = useMiniGames(state => state.setFavoriteGames);
  const storeFavoriteGames = useMiniGames(state => state.favoriteGames);
  const getGameById = useGameMetadataStore(state => state.getGameById);
  const {addFavoriteGame} = useAddFavoriteGame();
  const {deleteFavoriteGame} = useDeleteFavoriteGame();

  const displayGames = useMemo(
    () => recentGames.map(game => normalizeCarouselGame(game, getGameById)),
    [recentGames, getGameById]
  );

  const isGameFavorited = useCallback(
    gameId => storeFavoriteGames.some(f => f.game_id === gameId),
    [storeFavoriteGames]
  );

  const handleFavorite = useCallback(
    game => {
      setFavoriteGames(
        {id: game.id, provider: game.provider, game_provider: game.provider},
        addFavoriteGame,
        deleteFavoriteGame
      );
    },
    [setFavoriteGames, addFavoriteGame, deleteFavoriteGame]
  );

  const handleGameClick = useCallback(
    (provider, gameId) => {
      navigate(`/games/${provider}/${gameId}`);
    },
    [navigate]
  );

  if (authStatus !== "authenticated" || (!isPending && recentGames.length === 0)) {
    return null;
  }

  return (
    <div
      id="recentlyPlayedGames"
      className={styles.container}
    >
      <HeadingCarousel
        title={t("recentlyPlayedTitle")}
        subtitle={t("recentlyPlayedSubTitle")}
        icon="▶️"
        onPrev={carouselApi?.scrollPrev}
        onNext={carouselApi?.scrollNext}
      />
      <Carousel
        onInit={setCarouselApi}
        renderItems={() =>
          isPending || recentGames.length === 0
            ? ["skel-1", "skel-2", "skel-3", "skel-4", "skel-5", "skel-6"].map(key => (
                <CarouselGameCard
                  key={key}
                  loading
                />
              ))
            : displayGames.map((game, index) => (
                <CarouselGameCard
                  key={recentGames[index].uniqueId}
                  name={game.name}
                  icon={game.icon}
                  providerLabel={game.providerLabel}
                  isFav={isGameFavorited(game.id)}
                  onFavorite={() => handleFavorite(game)}
                  onClick={() => handleGameClick(game.provider, game.id)}
                />
              ))
        }
      />
    </div>
  );
};
